/**
 * Motion-based ball detector (no model required).
 *
 * A pragmatic fallback for when no trained TrackNet is available: it diffs
 * consecutive frames to build a motion mask, groups moving pixels into blobs
 * (connected components), and keeps small, compact, roughly-round blobs that
 * look ball-like — rejecting the large blobs produced by players. When a
 * previous ball position is supplied it prefers the nearest candidate to keep
 * the track continuous.
 *
 * Runs fully in-browser on a downscaled work canvas, so it's fast and needs no
 * network/weights. Accuracy is well below TrackNet (it struggles with camera
 * motion, occlusion, and busy backgrounds) — a stopgap, not a replacement.
 */

export const defaultMotionConfig = {
  workWidth: 320, // downscaled processing width (px)
  diffThreshold: 22, // per-pixel gray difference counted as motion
  minAreaFrac: 0.00003, // min blob area as a fraction of the work canvas
  maxAreaFrac: 0.02, // max blob area (excludes big player/motion blobs)
  minFill: 0.35, // area / bbox-area — compactness (round ≈ high)
  maxAspect: 2.5, // reject very elongated blobs
  searchRadiusFrac: 0.25, // prefer blobs within this fraction of width of prev
};

function pickSmallest(cands) {
  return cands.reduce((m, c) => (c.area < m.area ? c : m), cands[0]);
}

export class MotionBallDetector {
  constructor(cfg = {}) {
    this.cfg = { ...defaultMotionConfig, ...cfg };
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d", { willReadFrequently: true });
    this.prev = null; // Uint8Array gray of previous frame
    this.w = 0;
    this.h = 0;
  }

  reset() {
    this.prev = null;
  }

  /** Returns { x, y, score } in source pixels, or null. */
  detect(source, prevPoint) {
    const srcW = source.videoWidth || source.width || source.naturalWidth;
    const srcH = source.videoHeight || source.height || source.naturalHeight;
    if (!srcW || !srcH) return null;

    const ww = this.cfg.workWidth;
    const wh = Math.max(1, Math.round((srcH / srcW) * ww));
    if (this.w !== ww || this.h !== wh) {
      this.w = ww;
      this.h = wh;
      this.canvas.width = ww;
      this.canvas.height = wh;
      this.prev = null;
    }

    this.ctx.drawImage(source, 0, 0, ww, wh);
    const { data } = this.ctx.getImageData(0, 0, ww, wh);
    const n = ww * wh;
    const gray = new Uint8Array(n);
    for (let i = 0, j = 0; i < n; i++, j += 4) {
      gray[i] = (data[j] * 77 + data[j + 1] * 150 + data[j + 2] * 29) >> 8;
    }

    if (!this.prev) {
      this.prev = gray;
      return null; // need two frames
    }

    // motion mask
    const thr = this.cfg.diffThreshold;
    const mask = new Uint8Array(n);
    const prev = this.prev;
    for (let i = 0; i < n; i++) {
      const d = gray[i] - prev[i];
      if ((d < 0 ? -d : d) > thr) mask[i] = 1;
    }
    this.prev = gray;

    // connected components via iterative flood fill (4-connectivity)
    const minArea = Math.max(1, this.cfg.minAreaFrac * n);
    const maxArea = this.cfg.maxAreaFrac * n;
    const visited = new Uint8Array(n);
    const stack = [];
    const candidates = [];

    for (let start = 0; start < n; start++) {
      if (!mask[start] || visited[start]) continue;
      stack.length = 0;
      stack.push(start);
      visited[start] = 1;
      let area = 0;
      let sx = 0;
      let sy = 0;
      let minx = ww;
      let miny = wh;
      let maxx = 0;
      let maxy = 0;

      while (stack.length) {
        const p = stack.pop();
        const px = p % ww;
        const py = (p - px) / ww;
        area++;
        sx += px;
        sy += py;
        if (px < minx) minx = px;
        if (px > maxx) maxx = px;
        if (py < miny) miny = py;
        if (py > maxy) maxy = py;

        if (px > 0 && mask[p - 1] && !visited[p - 1]) {
          visited[p - 1] = 1;
          stack.push(p - 1);
        }
        if (px < ww - 1 && mask[p + 1] && !visited[p + 1]) {
          visited[p + 1] = 1;
          stack.push(p + 1);
        }
        if (py > 0 && mask[p - ww] && !visited[p - ww]) {
          visited[p - ww] = 1;
          stack.push(p - ww);
        }
        if (py < wh - 1 && mask[p + ww] && !visited[p + ww]) {
          visited[p + ww] = 1;
          stack.push(p + ww);
        }
      }

      if (area < minArea || area > maxArea) continue;
      const bw = maxx - minx + 1;
      const bh = maxy - miny + 1;
      const fill = area / (bw * bh);
      if (fill < this.cfg.minFill) continue;
      const ar = bw >= bh ? bw / bh : bh / bw;
      if (ar > this.cfg.maxAspect) continue;
      candidates.push({ cx: sx / area, cy: sy / area, area, fill });
    }

    if (!candidates.length) return null;

    // choose candidate: nearest to the previous ball position, else smallest
    let chosen = null;
    if (prevPoint) {
      const ppx = (prevPoint.x / srcW) * ww;
      const ppy = (prevPoint.y / srcH) * wh;
      const rad = this.cfg.searchRadiusFrac * ww;
      let bestD = Infinity;
      for (const c of candidates) {
        const d = Math.hypot(c.cx - ppx, c.cy - ppy);
        if (d < bestD && d < rad) {
          bestD = d;
          chosen = c;
        }
      }
    }
    if (!chosen) chosen = pickSmallest(candidates);

    return {
      x: (chosen.cx / ww) * srcW,
      y: (chosen.cy / wh) * srcH,
      score: Math.min(1, chosen.fill),
    };
  }

  dispose() {
    this.prev = null;
  }
}
